import { useEffect, useMemo, useState } from 'react'
import {Eye, EyeOff, Pencil, Trash2, RefreshCw, Search, X, Save, CheckCircle2, Circle, AlertTriangle, Clock, Zap, } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  useAvailableCandidates,
  formatProfileLabel,
  formatRemaining,
  type AvailableCandidate,
  type CandidateQualification,
  type CandidateMobility,
  type CandidateHousingNeed,
  type MobilityRadiusKm,
  CANDIDATE_QUALIFICATIONS,
  MOBILITY_LABELS,
  HOUSING_LABELS,
  FRENCH_REGIONS,
  MOBILITY_RADIUS_OPTIONS,
} from '@/hooks/useAvailableCandidates'

type StatusFilter = 'all' | 'published' | 'unpublished'
type AvailFilter = 'all' | 'now' | 'not_now'

function StatusBadge({ candidate }: { candidate: AvailableCandidate }) {
  const published = candidate.isPublished !== false
  const now = candidate.isAvailableNow === true
  const expired =
    !!candidate.availableUntil && new Date(candidate.availableUntil).getTime() < Date.now()
  return (
    <div className="flex flex-wrap gap-1.5">
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
          published && !expired ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-700'
        }`}
      >
        {published && !expired ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
        {published && !expired ? 'En ligne' : expired ? 'Expiré' : 'Désactivé'}
      </span>
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
          now ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'
        }`}
      >
        {now ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
        {now ? 'Dispo maintenant' : 'Non dispo'}
      </span>
    </div>
  )
}

export default function AvailableCandidatesAdmin() {
  const {
    items, loading, saving, error,
    fetchAll, adminUpdate, adminReactivate, adminDelete,
  } = useAvailableCandidates()

  const [search, setSearch] = useState('')
  const [qualifFilter, setQualifFilter] = useState<'all' | CandidateQualification>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [availFilter, setAvailFilter] = useState<AvailFilter>('all')
  const [editing, setEditing] = useState<AvailableCandidate | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<AvailableCandidate | null>(null)

  // Refresh tick (compte à rebours)
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    void fetchAll()
  }, [fetchAll])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter((c) => {
      if (qualifFilter !== 'all' && c.qualification !== qualifFilter) return false
      const expired =
        !!c.availableUntil && new Date(c.availableUntil).getTime() < Date.now()
      const published = c.isPublished !== false && !expired
      if (statusFilter === 'published' && !published) return false
      if (statusFilter === 'unpublished' && published) return false
      if (availFilter === 'now' && c.isAvailableNow !== true) return false
      if (availFilter === 'not_now' && c.isAvailableNow === true) return false
      if (!q) return true
      return (
        c.publicId?.toLowerCase().includes(q) ||
        c.qualification?.toLowerCase().includes(q) ||
        c.city?.toLowerCase().includes(q) ||
        c.region?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.creatorEmail?.toLowerCase().includes(q)
      )
    })
  }, [items, search, qualifFilter, statusFilter, availFilter])

  const counts = useMemo(() => {
    const total = items.length
    const published = items.filter((c) => {
      const expired =
        !!c.availableUntil && new Date(c.availableUntil).getTime() < Date.now()
      return c.isPublished !== false && !expired
    }).length
    const now = items.filter((c) => c.isAvailableNow === true).length
    return { total, published, now }
  }, [items])

  async function togglePublished(c: AvailableCandidate) {
    try {
      const next = !(c.isPublished !== false)
      await adminUpdate(c._id, { isPublished: next })
      toast.success(next ? 'Profil activé' : 'Profil désactivé')
    } catch {
      toast.error('Échec de la mise à jour')
    }
  }

  async function reactivate48h(c: AvailableCandidate) {
    try {
      await adminReactivate(c._id)
      toast.success('Profil réactivé pour 48h')
    } catch {
      toast.error('Échec de la réactivation')
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return
    try {
      await adminDelete(confirmDelete._id)
      toast.success('Profil supprimé')
      setConfirmDelete(null)
    } catch {
      toast.error('Échec de la suppression')
    }
  }

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">CVthèque — Disponibles Immédiatement !</h2>
          <p className="text-sm text-gray-600">
            {counts.total} profil{counts.total > 1 ? 's' : ''} • {counts.published} en ligne •{' '}
            {counts.now} disponible{counts.now > 1 ? 's' : ''} maintenant
          </p>
        </div>
        <button
          type="button"
          onClick={() => void fetchAll()}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </header>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Filtres */}
      <div className="grid gap-3 md:grid-cols-4">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher : identifiant, ville, région, email, téléphone…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={qualifFilter}
          onChange={(e) => setQualifFilter(e.target.value as 'all' | CandidateQualification)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
        >
          <option value="all">Toutes qualifications</option>
          {CANDIDATE_QUALIFICATIONS.map((q) => (
            <option key={q} value={q}>{q}</option>
          ))}
        </select>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
          >
            <option value="all">Tout statut</option>
            <option value="published">En ligne</option>
            <option value="unpublished">Hors ligne / Expiré</option>
          </select>
          <select
            value={availFilter}
            onChange={(e) => setAvailFilter(e.target.value as AvailFilter)}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
          >
            <option value="all">Toute dispo</option>
            <option value="now">Dispo maintenant</option>
            <option value="not_now">Non dispo</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Identifiant</th>
              <th className="px-3 py-2 text-left font-semibold">Localisation</th>
              <th className="px-3 py-2 text-left font-semibold">Mobilité</th>
              <th className="px-3 py-2 text-left font-semibold">Logement</th>
              <th className="px-3 py-2 text-left font-semibold">Téléphone</th>
              <th className="px-3 py-2 text-left font-semibold">Email</th>
              <th className="px-3 py-2 text-left font-semibold">Encore dispo</th>
              <th className="px-3 py-2 text-left font-semibold">Statut</th>
              <th className="px-3 py-2 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center text-gray-500">
                  {loading ? 'Chargement…' : 'Aucun profil ne correspond aux filtres.'}
                </td>
              </tr>
            )}
            {filtered.map((c) => {
              const expired =
                !!c.availableUntil && new Date(c.availableUntil).getTime() < Date.now()
              const published = c.isPublished !== false && !expired
              const remaining = c.availableUntil ? formatRemaining(c.availableUntil) : ''
              const profileLabel = formatProfileLabel(c.qualification, c.publicId)
              return (
                <tr key={c._id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 align-top min-w-[160px]">
                    <div className="font-mono font-bold text-gray-900 break-all">{profileLabel}</div>
                    <div className="text-xs text-gray-500 break-all">{c.creatorEmail || '—'}</div>
                    {c.isAvailableNow && (
                      <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <Zap className="w-3 h-3" />
                        Maintenant
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-gray-900">{c.city || '—'}</div>
                    <div className="text-xs text-gray-500">{c.region || '—'}</div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div>{MOBILITY_LABELS[c.mobility]}</div>
                    {c.mobility === 'locale' && c.mobilityRadiusKm && (
                      <div className="text-xs text-gray-500">{c.mobilityRadiusKm} km</div>
                    )}
                    {c.mobility === 'departementale' && c.mobilityDepartment && (
                      <div className="text-xs text-gray-500">{c.mobilityDepartment}</div>
                    )}
                    {c.mobility === 'regionale' && c.mobilityRegion && (
                      <div className="text-xs text-gray-500">{c.mobilityRegion}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top whitespace-nowrap">
                    {HOUSING_LABELS[c.housingNeed]}
                  </td>
                  <td className="px-3 py-2 align-top whitespace-nowrap">{c.phone || '—'}</td>
                  <td className="px-3 py-2 align-top break-all">{c.email || '—'}</td>
                  <td className="px-3 py-2 align-top whitespace-nowrap">
                    <div className="flex items-center gap-1 text-xs">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      <span className={expired ? 'text-rose-700 font-semibold' : 'text-gray-700'}>
                        {remaining || '—'}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <StatusBadge candidate={c} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <button
                        type="button"
                        onClick={() => void reactivate48h(c)}
                        disabled={saving}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md border border-blue-300 text-blue-800 bg-blue-50 hover:bg-blue-100"
                        title="Réactiver pour 48h"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        +48h
                      </button>
                      <button
                        type="button"
                        onClick={() => void togglePublished(c)}
                        disabled={saving}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md border ${
                          published
                            ? 'border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100'
                            : 'border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100'
                        }`}
                        title={published ? 'Désactiver' : 'Activer'}
                      >
                        {published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        {published ? 'Désactiver' : 'Activer'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(c)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md border border-blue-300 text-blue-800 bg-blue-50 hover:bg-blue-100"
                        title="Modifier"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(c)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md border border-red-300 text-red-800 bg-red-50 hover:bg-red-100"
                        title="Supprimer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditCandidateModal
          candidate={editing}
          saving={saving}
          onCancel={() => setEditing(null)}
          onSave={async (patch) => {
            try {
              await adminUpdate(editing._id, patch)
              toast.success('Profil mis à jour')
              setEditing(null)
            } catch {
              toast.error('Échec de la mise à jour')
            }
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          candidate={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => void handleDelete()}
        />
      )}
    </section>
  )
}

/* ---------- Modal édition ---------- */

interface EditPatch {
  qualification?: CandidateQualification
  city?: string
  region?: string
  mobility?: CandidateMobility
  mobilityRadiusKm?: MobilityRadiusKm
  mobilityRegion?: string
  housingNeed?: CandidateHousingNeed
  phone?: string
  email?: string
  isPublished?: boolean
  isAvailableNow?: boolean
}

function EditCandidateModal({
  candidate,
  saving,
  onCancel,
  onSave,
}: {
  candidate: AvailableCandidate
  saving: boolean
  onCancel: () => void
  onSave: (patch: EditPatch) => Promise<void>
}) {
  const [qualification, setQualification] = useState<CandidateQualification>(candidate.qualification)
  const [city, setCity] = useState(candidate.city || '')
  const [region, setRegion] = useState(candidate.region || '')
  const [mobility, setMobility] = useState<CandidateMobility>(candidate.mobility)
  const [mobilityRadiusKm, setMobilityRadiusKm] = useState<MobilityRadiusKm | ''>(
    (candidate.mobilityRadiusKm as MobilityRadiusKm | undefined) ?? ''
  )
  const [mobilityRegion, setMobilityRegion] = useState(candidate.mobilityRegion || '')
  const [housingNeed, setHousingNeed] = useState<CandidateHousingNeed>(candidate.housingNeed)
  const [phone, setPhone] = useState(candidate.phone || '')
  const [emailVal, setEmailVal] = useState(candidate.email || '')
  const [isPublished, setIsPublished] = useState(candidate.isPublished !== false)
  const [isAvailableNow, setIsAvailableNow] = useState(candidate.isAvailableNow === true)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!city.trim() || !region.trim() || !phone.trim() || !emailVal.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires.')
      return
    }
    if (mobility === 'locale' && !mobilityRadiusKm) {
      toast.error('Sélectionnez un rayon (km) pour la mobilité locale.')
      return
    }
    if (mobility === 'regionale' && !mobilityRegion) {
      toast.error('Sélectionnez une région souhaitée.')
      return
    }
    await onSave({
      qualification, city, region,
      mobility,
      mobilityRadiusKm: mobility === 'locale' ? (mobilityRadiusKm as MobilityRadiusKm) : undefined,
      mobilityRegion: mobility === 'regionale' ? mobilityRegion : undefined,
      housingNeed, phone, email: emailVal,
      isPublished, isAvailableNow,
    })
  }

  const profileLabel = formatProfileLabel(qualification, candidate.publicId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/40 overflow-y-auto">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl my-6 max-h-[calc(100vh-3rem)] flex flex-col">
        <header className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Modifier le profil disponibilité</h3>
            <p className="text-xs font-mono text-gray-500 mt-0.5">{profileLabel}</p>
          </div>
          <button type="button" onClick={onCancel} className="p-1.5 rounded-md hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="p-4 space-y-3 overflow-y-auto">
          <Field label="Qualification *">
            <select value={qualification} onChange={(e) => setQualification(e.target.value as CandidateQualification)} className="input">
              {CANDIDATE_QUALIFICATIONS.map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Ville *">
              <input value={city} onChange={(e) => setCity(e.target.value)} className="input" required />
            </Field>
            <Field label="Région *">
              <select value={region} onChange={(e) => setRegion(e.target.value)} className="input" required>
                <option value="">— Choisir —</option>
                {FRENCH_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Téléphone *">
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="input" required />
            </Field>
            <Field label="Email *">
              <input type="email" value={emailVal} onChange={(e) => setEmailVal(e.target.value)} className="input" required />
            </Field>
          </div>

          <Field label="Mobilité *">
            <select value={mobility} onChange={(e) => setMobility(e.target.value as CandidateMobility)} className="input">
              <option value="non">{MOBILITY_LABELS.non}</option>
              <option value="locale">{MOBILITY_LABELS.locale}</option>
              <option value="departementale">{MOBILITY_LABELS.departementale}</option>
              <option value="regionale">{MOBILITY_LABELS.regionale}</option>
              <option value="france">{MOBILITY_LABELS.france}</option>
            </select>
          </Field>

          {mobility === 'locale' && (
            <Field label="Rayon (km) *">
              <select
                value={mobilityRadiusKm === '' ? '' : String(mobilityRadiusKm)}
                onChange={(e) => setMobilityRadiusKm(e.target.value === '' ? '' : (Number(e.target.value) as MobilityRadiusKm))}
                className="input"
                required
              >
                <option value="">— Choisir —</option>
                {MOBILITY_RADIUS_OPTIONS.map((r) => <option key={r} value={r}>{r} km</option>)}
              </select>
            </Field>
          )}

          {mobility === 'regionale' && (
            <Field label="Région souhaitée *">
              <select value={mobilityRegion} onChange={(e) => setMobilityRegion(e.target.value)} className="input" required>
                <option value="">— Choisir —</option>
                {FRENCH_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          )}

          <Field label="Logement *">
            <select value={housingNeed} onChange={(e) => setHousingNeed(e.target.value as CandidateHousingNeed)} className="input">
              <option value="non">{HOUSING_LABELS.non}</option>
              <option value="oui">{HOUSING_LABELS.oui}</option>
              <option value="oui_si_eloigne">{HOUSING_LABELS.oui_si_eloigne}</option>
            </select>
          </Field>

          <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-100">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm font-semibold">Publié dans la CVthèque</span>
            </label>
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={isAvailableNow}
                onChange={(e) => setIsAvailableNow(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm font-semibold">Disponible maintenant</span>
            </label>
          </div>

          <footer className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 bg-white hover:bg-gray-50">
              Annuler
            </button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              <Save className="w-4 h-4" />
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </footer>
        </form>
      </div>

      <style>{`.input{display:block;width:100%;padding:.5rem .75rem;font-size:.875rem;border:1px solid #d1d5db;border-radius:.5rem;background:#fff}.input:focus{outline:none;border-color:#3b82f6;box-shadow:0 0 0 2px rgba(59,130,246,.2)}`}</style>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  )
}

function ConfirmDeleteModal({
  candidate,
  onCancel,
  onConfirm,
}: {
  candidate: AvailableCandidate
  onCancel: () => void
  onConfirm: () => void
}) {
  const profileLabel = formatProfileLabel(candidate.qualification, candidate.publicId)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/40">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-5">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Supprimer ce profil&nbsp;?</h3>
        <p className="text-sm text-gray-600 mb-4">
          Vous êtes sur le point de supprimer définitivement le profil disponibilité{' '}
          <strong className="font-mono">{profileLabel}</strong>. Cette action est irréversible.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 bg-white hover:bg-gray-50">
            Annuler
          </button>
          <button type="button" onClick={onConfirm} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700">
            <Trash2 className="w-4 h-4" />
            Supprimer
          </button>
        </div>
      </div>
    </div>
  )
}
